import { useState, useMemo, useRef } from "react";
import { toast } from "sonner";
import QRCode from "qrcode";
import { Copy, Download, RefreshCw, Check, X } from "lucide-react";
import { Field, TextInput, TextArea, Select, PrimaryButton, GhostButton, ResultStat, ShareButton } from "@/tools/ui";
import { downloadDataUrl } from "@/lib/download";

export const WordCounter = () => {
  const [text, setText] = useState("");
  const stats = useMemo(() => {
    const words = (text.trim().match(/\S+/g) || []).length;
    const chars = text.length;
    const charsNoSpace = text.replace(/\s/g, "").length;
    const sentences = (text.match(/[.!?]+(\s|$)/g) || []).length;
    const paragraphs = (text.trim() ? text.trim().split(/\n+/).length : 0);
    const readMin = Math.max(1, Math.round(words / 200));
    return { words, chars, charsNoSpace, sentences, paragraphs, readMin };
  }, [text]);

  return (
    <div className="space-y-6">
      <Field label="Your text">
        <TextArea data-testid="wc-input" rows={8} value={text} onChange={(e) => setText(e.target.value)} placeholder="Start typing or paste text…" />
      </Field>
      <div data-testid="wc-result" className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <ResultStat label="Words" value={stats.words} accent />
        <ResultStat label="Characters" value={stats.chars} />
        <ResultStat label="No Spaces" value={stats.charsNoSpace} />
        <ResultStat label="Sentences" value={stats.sentences} />
        <ResultStat label="Paragraphs" value={stats.paragraphs} />
        <ResultStat label="Read Time" value={`${stats.readMin} min`} />
      </div>
    </div>
  );
};

export const QrGenerator = () => {
  const [text, setText] = useState("https://");
  const [url, setUrl] = useState("");

  const gen = async () => {
    if (!text.trim()) return toast.error("Enter text or a link");
    try {
      const dataUrl = await QRCode.toDataURL(text, { width: 512, margin: 2, color: { dark: "#0A0A0A", light: "#FFFFFF" } });
      setUrl(dataUrl);
    } catch { toast.error("Could not generate QR"); }
  };

  return (
    <div className="space-y-6">
      <Field label="Text or URL">
        <TextInput data-testid="qr-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="https://example.com" />
      </Field>
      <PrimaryButton data-testid="qr-run" onClick={gen}>Generate QR Code</PrimaryButton>
      {url && (
        <div className="space-y-4">
          <div className="flex justify-center border border-border bg-muted p-6">
            <img data-testid="qr-output" src={url} alt="QR code" className="h-56 w-56" />
          </div>
          <div className="flex flex-wrap gap-3">
            <PrimaryButton data-testid="qr-download" onClick={() => downloadDataUrl(url, "qrcode.png")}><Download className="h-4 w-4" /> Download PNG</PrimaryButton>
            <ShareButton testid="qr-share" text={text} title="QR content from TOOL/BOX" className="px-5 py-2.5 text-sm" />
          </div>
        </div>
      )}
    </div>
  );
};

export const PasswordGenerator = () => {
  const [len, setLen] = useState(16);
  const [opts, setOpts] = useState({ upper: true, lower: true, num: true, sym: true });
  const [pw, setPw] = useState("");

  const gen = () => {
    const sets = {
      upper: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
      lower: "abcdefghijklmnopqrstuvwxyz",
      num: "0123456789",
      sym: "!@#$%^&*()-_=+[]{};:,.<>?",
    };
    const pool = Object.keys(opts).filter((k) => opts[k]).map((k) => sets[k]).join("");
    if (!pool) return toast.error("Select at least one character type");
    const arr = new Uint32Array(len);
    crypto.getRandomValues(arr);
    let out = "";
    for (let i = 0; i < len; i++) out += pool[arr[i] % pool.length];
    setPw(out);
  };

  const strength = pw.length >= 16 ? "Very Strong" : pw.length >= 12 ? "Strong" : pw.length >= 8 ? "Medium" : "Weak";
  const toggle = (k) => setOpts((o) => ({ ...o, [k]: !o[k] }));

  return (
    <div className="space-y-6">
      <Field label={`Length: ${len}`}>
        <input data-testid="pw-length" type="range" min="6" max="40" value={len} onChange={(e) => setLen(+e.target.value)} className="w-full accent-primary" />
      </Field>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[["upper", "A-Z"], ["lower", "a-z"], ["num", "0-9"], ["sym", "!@#"]].map(([k, label]) => (
          <button
            key={k}
            data-testid={`pw-${k}`}
            onClick={() => toggle(k)}
            className={`flex items-center justify-center gap-2 border px-3 py-2.5 text-sm font-bold transition-colors ${opts[k] ? "border-primary bg-primary text-white" : "border-border bg-card"}`}
          >
            {opts[k] ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />} {label}
          </button>
        ))}
      </div>
      <PrimaryButton data-testid="pw-run" onClick={gen}><RefreshCw className="h-4 w-4" /> Generate Password</PrimaryButton>
      {pw && (
        <div className="border border-border bg-muted">
          <div className="flex items-center justify-between border-b border-border px-4 py-2">
            <span className="overline text-muted-foreground">Strength: {strength}</span>
            <div className="flex items-center gap-2">
              <GhostButton data-testid="pw-copy" onClick={() => { navigator.clipboard.writeText(pw); toast.success("Copied"); }} className="px-3 py-1.5 text-xs"><Copy className="h-3 w-3" /> Copy</GhostButton>
              <ShareButton testid="pw-share" text={pw} title="Password from TOOL/BOX" />
            </div>
          </div>
          <p data-testid="pw-output" className="break-all px-4 py-4 font-mono-tech text-lg">{pw}</p>
        </div>
      )}
    </div>
  );
};

export const JsonFormatter = () => {  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [error, setError] = useState("");

  const run = (minify) => {
    try {
      const parsed = JSON.parse(input);
      setOutput(JSON.stringify(parsed, null, minify ? 0 : 2));
      setError("");
    } catch (e) {
      setError(e.message);
      setOutput("");
    }
  };

  return (
    <div className="space-y-6">
      <Field label="Paste JSON">
        <TextArea data-testid="json-input" rows={7} value={input} onChange={(e) => setInput(e.target.value)} placeholder='{"hello":"world","items":[1,2,3]}' className="font-mono-tech" />
      </Field>
      <div className="flex flex-wrap gap-3">
        <PrimaryButton data-testid="json-beautify" onClick={() => run(false)}>Beautify</PrimaryButton>
        <GhostButton data-testid="json-minify" onClick={() => run(true)}>Minify</GhostButton>
      </div>
      {error && <div data-testid="json-error" className="border border-[#E53935] bg-destructive/10 px-4 py-3 text-sm text-[#E53935]">Invalid JSON: {error}</div>}
      {output && (
        <div className="border border-border bg-muted">
          <div className="flex items-center justify-between border-b border-border px-4 py-2">
            <span className="overline text-muted-foreground">Output</span>
            <div className="flex items-center gap-2">
              <GhostButton data-testid="json-copy" onClick={() => { navigator.clipboard.writeText(output); toast.success("Copied"); }} className="px-3 py-1.5 text-xs"><Copy className="h-3 w-3" /> Copy</GhostButton>
              <ShareButton testid="json-share" text={output} title="JSON from TOOL/BOX" />
            </div>
          </div>
          <pre data-testid="json-output" className="overflow-x-auto px-4 py-4 font-mono-tech text-sm">{output}</pre>
        </div>
      )}
    </div>
  );
};


export const PasswordStrength = () => {
  const [pw, setPw] = useState("");
  const score = (() => {
    let s = 0;
    if (pw.length >= 8) s++;
    if (pw.length >= 12) s++;
    if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s++;
    if (/[0-9]/.test(pw)) s++;
    if (/[^a-zA-Z0-9]/.test(pw)) s++;
    return Math.min(s, 5);
  })();
  const labels = ["Very Weak", "Weak", "Fair", "Good", "Strong", "Very Strong"];
  const colors = ["#E53935", "#E53935", "#FFC107", "#FFC107", "#002FA7", "#002FA7"];
  return (
    <div className="space-y-6">
      <Field label="Enter a password to test">
        <TextInput data-testid="pws-input" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Type a password…" className="font-mono-tech" />
      </Field>
      {pw && (
        <div data-testid="pws-result" className="space-y-3">
          <div className="flex gap-1.5">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-2 flex-1 border border-border" style={{ backgroundColor: i < score ? colors[score] : "#F1F3F5" }} />
            ))}
          </div>
          <p className="font-heading text-2xl font-black tracking-tight" style={{ color: colors[score] }}>{labels[score]}</p>
          <p className="text-sm text-muted-foreground">{pw.length} characters · use 12+ chars with upper, lower, numbers and symbols for best strength.</p>
        </div>
      )}
    </div>
  );
};
